<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// Short commit hash, as git prints it
const COMMIT_DISPLAY_LENGTH = 7;

const version = ref("");
onMounted(async () => {
  try {
    const response = await fetch("/api/health");
    const body = (await response.json()) as { version?: string; commit?: string };
    version.value = [body.version, body.commit?.slice(0, COMMIT_DISPLAY_LENGTH)].filter(Boolean).join(" · ");
  } catch {
    // The page renders without a reachable backend: the version stays empty.
  }
});
</script>

<template>
  <section class="hero">
    <h1 class="title">
      {{ t("accueil.titre") }}
    </h1>
    <p class="subtitle">
      {{ t("accueil.sousTitre") }}
    </p>
    <p class="lead">
      {{ t("accueil.accroche") }}
    </p>

    <nav class="entry-links">
      <RouterLink
        class="entry-link entry-link-primary"
        :to="{ name: 'signup' }"
      >
        {{ t("auth.entry.signUp") }}
      </RouterLink>
      <RouterLink
        class="entry-link"
        :to="{ name: 'login' }"
      >
        {{ t("auth.entry.signIn") }}
      </RouterLink>
    </nav>

    <div class="status-row">
      <span class="status">{{ t("accueil.socle") }}</span>
      <span
        v-if="version"
        class="version"
      >{{ version }}</span>
    </div>
  </section>
</template>

<style scoped>
.hero {
  max-width: 44rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-12) var(--space-6);
}

.title {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--size-hero);
  font-weight: var(--weight-bold);
  line-height: var(--leading-tight);
  color: var(--color-text);
}

.subtitle {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--size-xl);
  font-weight: var(--weight-semibold);
  line-height: var(--leading-snug);
  color: var(--color-text);
}

.lead {
  margin: 0;
  font-size: var(--size-lg);
  color: var(--color-text-muted);
}

.entry-links {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-top: var(--space-2);
}

.entry-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: var(--space-2) var(--space-6);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-text);
  text-decoration: none;
  transition: background var(--transition-fast), border-color var(--transition-fast);
}

.entry-link:hover {
  background: var(--color-bg-sunken);
}

/* The sign-up call: the one place the orange appears (DESIGN.md, --cta-*) */
.entry-link-primary {
  border-color: var(--cta-bg);
  background: var(--cta-bg);
  color: var(--cta-ink);
}

.entry-link-primary:hover {
  border-color: var(--cta-bg-hover);
  background: var(--cta-bg-hover);
}

.status-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-4);
  margin-top: var(--space-8);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border);
}

.status {
  font-size: var(--size-sm);
  color: var(--color-text-muted);
}

.version {
  font-family: var(--font-numeric);
  font-variant-numeric: tabular-nums;
  font-size: var(--size-xs);
  color: var(--color-text-muted);
  overflow-wrap: anywhere;
}

@media (max-width: 560px) {
  .hero {
    padding: var(--space-8) var(--space-4);
  }
}
</style>
